import { motion } from "framer-motion";
import { 
  Target, 
  Users, 
  BarChart3, 
  TrendingUp,
  Building2,
  MapPin
} from "lucide-react";

const VISHAL_IMAGE = "https://customer-assets.emergentagent.com/job_5732e08b-7d3d-4e32-a1fe-aa008b7cf2dd/artifacts/tgtqp5nf_Generated%20Image%20November%2029%2C%202025%20-%207_49PM.png";

const brands = [
  "Kotak Mahindra Bank",
  "Tata Real Estate", 
  "Sony Entertainment",
  "Enamor",
  "Deconstruct",
  "BookMyChef",
  "Pizza Hut",
  "Orange Telecom",
  "Royal Jordanian Airline",
  "Bank of Jordan"
];

const expertise = [
  {
    category: "Paid Media Expertise",
    items: ["Google Ads (UAC, Search, Display)", "Meta Ads (Facebook & Instagram)", "Apple Search Ads (ASA)", "TikTok Ads", "Snapchat Ads", "Programmatic Advertising"]
  },
  {
    category: "Analytics & Measurement",
    items: ["Google Analytics (GA4)", "Firebase Analytics", "Mobile Measurement Partners (MMPs)", "Attribution Modeling", "S2S & CAPI Integration"]
  },
  {
    category: "Strategy & Optimization",
    items: ["User Acquisition Strategy", "Retention Marketing", "CAC:LTV Optimization", "ROAS Improvement", "A/B Testing", "Campaign Management"]
  }
];

const industries = [
  "Fintech", "Retail", "Beauty & Cosmetics", "E-Commerce", "Food & Beverages", "Telecom", "AI & Technology"
];

const About = () => {
  return (
    <section id="about" className="py-20 md:py-28" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm text-[#007AFF] font-medium uppercase tracking-wider">About Us</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mt-4 mb-6">
            Driving Measurable Growth for <br />
            <span className="gradient-text">Mobile Apps & E-Commerce</span>
          </h2>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-20">
          {/* Left - Image & Profile */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative">
              <div className="gradient-orb gradient-orb-blue w-40 h-40 -top-10 -left-10 absolute opacity-50"></div>
              
              <div className="relative z-10 rounded-2xl overflow-hidden border border-[#30363D]">
                <img
                  src={VISHAL_IMAGE}
                  alt="Vishal Mehta - Founder of North Stack"
                  className="w-full h-auto"
                  data-testid="founder-image"
                />
              </div>
            </div>

            {/* Profile Card - Below Image */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-6 bg-[#1A1F26] rounded-xl p-5 border border-[#30363D]"
            >
              <h3 className="text-xl font-bold mb-1">Vishal Mehta</h3>
              <p className="text-[#007AFF] text-sm font-medium mb-3">Founder & CEO</p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-[#8B949E] text-sm">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span>Dubai, UAE</span>
                </div>
                <div className="flex items-center gap-2 text-[#8B949E] text-sm">
                  <Building2 className="w-4 h-4 flex-shrink-0" />
                  <span>Ex - WPP Media & Publicis Groupe</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg text-[#8B949E] leading-relaxed mb-8">
              Expert Digital Marketer with over 12+ years of experience in App & Web E-commerce 
              Performance media marketing, media buying, strategic media planning, paid media, 
              execution and optimization of integrated digital media marketing campaigns.
            </p>

            {/* Highlight Cards */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="p-4 rounded-xl bg-[#1A1F26] border border-[#30363D]">
                <Target className="w-6 h-6 text-[#007AFF] mb-2" />
                <h4 className="font-semibold mb-1">Strategic Planning</h4>
                <p className="text-sm text-[#8B949E]">Data-driven growth strategies</p>
              </div>
              <div className="p-4 rounded-xl bg-[#1A1F26] border border-[#30363D]">
                <Users className="w-6 h-6 text-[#00C853] mb-2" />
                <h4 className="font-semibold mb-1">User Acquisition</h4>
                <p className="text-sm text-[#8B949E]">Quality installs & conversions</p>
              </div>
              <div className="p-4 rounded-xl bg-[#1A1F26] border border-[#30363D]">
                <BarChart3 className="w-6 h-6 text-[#007AFF] mb-2" />
                <h4 className="font-semibold mb-1">Performance Analytics</h4>
                <p className="text-sm text-[#8B949E]">Advanced attribution & tracking</p>
              </div>
              <div className="p-4 rounded-xl bg-[#1A1F26] border border-[#30363D]">
                <TrendingUp className="w-6 h-6 text-[#00C853] mb-2" />
                <h4 className="font-semibold mb-1">ROAS Optimization</h4>
                <p className="text-sm text-[#8B949E]">Maximize return on ad spend</p>
              </div>
            </div>

            <ul className="space-y-3 text-[#8B949E]">
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF] mt-2"></span>
                12+ years of experience leading growth strategies across B2C verticals, managing monthly marketing budgets in 6 figures.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00C853] mt-2"></span>
                Proven track record of developing and executing multi-channel growth strategies, driving measurable results.
              </li>
              <li className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF] mt-2"></span>
                Expertise in optimizing key KPIs including CAC, LTV, user growth, ROAS and engagement.
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Brands Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-xl font-bold mb-6 text-center">Worked with Leading Brands</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {brands.map((brand, index) => (
              <motion.span
                key={brand}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="px-4 py-2 rounded-full bg-[#1A1F26] border border-[#30363D] text-sm text-[#8B949E] hover:border-[#007AFF]/50 hover:text-white transition-all"
              >
                {brand}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Expertise Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {expertise.map((exp, index) => (
            <motion.div
              key={exp.category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-6 rounded-2xl bg-[#1A1F26] border border-[#30363D]"
            >
              <h4 className="font-bold mb-4 text-lg">{exp.category}</h4>
              <ul className="space-y-2">
                {exp.items.map((item) => (
                  <li key={item} className="text-sm text-[#8B949E] flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-gradient-to-r from-[#007AFF] to-[#00C853]"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Industries */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h3 className="text-xl font-bold mb-6">Industries Served</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {industries.map((industry, index) => (
              <motion.span
                key={industry}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="px-5 py-2.5 rounded-full bg-gradient-to-r from-[#007AFF]/10 to-[#00C853]/10 border border-[#007AFF]/30 text-sm font-medium hover:border-[#007AFF] transition-colors"
              >
                {industry}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
